import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import ImageAvatar from 'components/uiLibrary/ImageAvatar';
import KebabMenu from 'components/uiLibrary/KebabMenu';
import LinkButton, { PrimaryButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import TabsNavigation from 'components/uiLibrary/TabsNavigation';
import Typography from 'components/uiLibrary/Typography';

// NOTE: We need to fix dependency cycle here
// eslint-disable-next-line import/no-cycle
import QuickFilters from 'components/Filters/Common/QuickFilters';
import AppliedFilters from 'components/Filters/Display/AppliedFilters';
import FilterFacets, { FILTER_LAYOUT } from 'components/Filters/Display/FilterFacets';
import useTracking from 'components/Globals/Analytics';
import {
  COMPONENTS,
  GOOGLE_OLD_TRACKING_SERVICES,
  SECTIONS,
  SUB_COMPONENTS,
} from 'components/Globals/Analytics/constants';
import CastingToolPromo from 'components/Globals/CastingToolPromo';
import ShareEntity from 'components/Globals/ShareEntity';
import CalendarHeaders from 'components/Productions/Display/CalendarProductionListing/Calendar/CalendarHeaders';
import CalendarRangeNavigation from 'components/Productions/Display/CalendarProductionListing/Calendar/CalendarRangeNavigation';

import { TP } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import useEntitySectionTabs from 'utils/hooks/useEntitySectionTabs';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';
import useSelectedViewMode from 'utils/hooks/useSelectedViewMode';
import useSharedCalendarRange from 'utils/hooks/useSharedCalendarRange';
import { ARTIST_PRO_VIEW, TRACK_EVENTS } from 'utils/tracking';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, FILTER_TYPES, ROUTE_RESERVED_KEYWORDS, VIEW_MODES } from 'operabase-router/constants';

import classes from './PublicViewStickyHeader.module.scss';

const trackingData = {
  section: SECTIONS.PUBLIC_VIEW_STICKY_HEADER,
};

const customFilterGroup = {
  [FILTER_LAYOUT.BUTTON_GROUP_DETAILED]: [
    { name: `${TP}.FN_GENRE`, filterTypes: [FILTER_TYPES.CREATOR] },
    { name: `${TP}.m_PROD`, filterTypes: [FILTER_TYPES.WORK] },
    { name: `${TP}.FN_PRODUCER`, filterTypes: [FILTER_TYPES.PRODUCER] },
    { name: `${TP}.WHO`, filterTypes: [FILTER_TYPES.WHO] },
    { name: `${TP}.WHERE`, filterTypes: [FILTER_TYPES.WHERE] },
    {
      name: `${TP}.FN_ALL`,
      filterTypes: [FILTER_TYPES.TICKET],
    },
  ],
};

const useActionItems = ({ trackingConfig = trackingData, onClickHandler }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isLoggedIn, obRouteContext } = useAppContext();
  const { setIsLoginDialog } = useDialogs();
  const { page } = obRouteContext || {};
  const { entity, entityType } = page;

  return useMemo(() => {
    const items = [];

    items.push(
      <ShareEntity
        className={classes.shareEntity}
        trackingData={{ section: SECTIONS.HEADER, component: COMPONENTS.SHARE_CTA }}
        asKebabMenu
      />,
    );

    items.push(
      <div key="login" className={classes.castingToolKebabMenuItem}>
        <Typography size="14" weight="medium">
          {t(`${TP}.FN_PLAN_FEATURE_5`)}
        </Typography>
        {entityType === ENTITY_TYPES.PROFILE && (
          <CastingToolPromo
            entityType={entityType}
            entity={entity}
            onClick={onClickHandler}
            styles={{ castingToolCta: classes.castingToolPromo }}
            trackingData={{
              ...trackingData,
              component: COMPONENTS.VIEW_IN_CASTING_TOOL_CTA,
            }}
            asCTA
          />
        )}
      </div>,
    );

    if (!isLoggedIn) {
      items.push(
        <div key="login" className={classes.login}>
          <Typography size="14" weight="medium">
            {t(`${TP}.m_LOGIN`)}
          </Typography>
          <PrimaryButton
            key="login"
            onClick={() => setIsLoginDialog({ isOpen: true })}
            shape="rectangle"
            trackingData={{
              ...trackingConfig,
              subComponent: SUB_COMPONENTS.LOGIN_CTA,
            }}
            leftIcon={<SpriteIcon icon="account_circle" size={14} className={classes.login__icon} />}
          >
            {t(`${TP}.m_LOGIN`)}
          </PrimaryButton>
        </div>,
      );
    }

    return items;
  }, [t, entityType, entity, isLoggedIn, trackingConfig, setIsLoginDialog]);
};

const Filters = ({ layout, mainPath, scrollable = true }) => (
  <FilterFacets
    layout={layout || FILTER_LAYOUT.ICON}
    scrollable={scrollable}
    mainPath={mainPath}
    trackingData={trackingData}
  />
);

const Profile = ({ entity }) => (
  <div
    className={classes.profile}
    onClick={() => {
      if (!window) {
        return;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }}
  >
    {entity?.image?.medium ? (
      <Image
        alt={entity.name}
        src={entity?.image?.medium}
        transformations={TRANSFORMATIONS.PROFILE_THUMBNAIL}
        disableNextImage
        lazy={false}
        width={34}
        height={34}
        className={classes.profile__image}
      />
    ) : (
      <ImageAvatar
        name={entity?.name}
        height={34}
        width={34}
        fontSize={16}
        shape="circle"
        type="V4"
        className={classes.profile__imageAvatar}
      />
    )}
    <Typography truncate>
      <Typography size="12" weight="bold" truncate>
        {entity?.name}
      </Typography>
      <Typography size="10">{entity?.headline}</Typography>
    </Typography>
    <span className={classes.profile__searchSection}>
      <SpriteIcon icon="search" size="16" />
    </span>
  </div>
);

const PublicViewStickyHeader = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const { isMobile } = useDeviceTypeLayouts();
  const isSmallScreen = useMediaQuery('(max-width: 800px) and (min-width: 600px)');
  const navigate = useNavigate();
  const { entity, entityType, mainPath, baseRoute } = usePageContext();
  const { setIsLoginDialog } = useDialogs();
  const { obRouteContext, isLoggedIn } = useAppContext();
  const { filterParams } = obRouteContext || {};
  const { filters } = obRouteContext?.linkProps || {};
  const {
    viewType,
    rangeStartDate,
    rangeEndDate,
    onShiftLeft,
    onShiftRight,
    allowLeftShift,
    allowRightShift,
  } = useSharedCalendarRange();
  const hasAppliedFilters = useMemo(() => {
    const appliedFilters = Object.keys(filterParams || {})?.filter(
      key =>
        ![
          FILTER_TYPES.BOOLEAN_SEARCH,
          FILTER_TYPES.SINCE_YEAR,
          FILTER_TYPES.BOOLEAN_SEARCH,
          FILTER_TYPES.SEARCH_EXPRESSION,
          FILTER_TYPES.CTX_ENTITY,
          FILTER_TYPES.DATE_FROM,
          FILTER_TYPES.DATE_TO,
        ].includes(key),
    );
    return Object.keys(appliedFilters).length > 0;
  }, [filterParams]);
  const tabs = useEntitySectionTabs({ entityType, entity, baseRoute, filters });
  const activeTab = tabs?.find(tab => tab.key === mainPath)?.key || tabs?.[0]?.key;
  const { viewMode } = useSelectedViewMode({ filters: obRouteContext?.filterParams });
  const isPerformancePage = mainPath === ROUTE_RESERVED_KEYWORDS.performances;
  const showCalendarFilters = viewMode === VIEW_MODES.CALENDAR && isPerformancePage;

  const onClickHandler = useCallback(() => {
    navigate.to(
      navigate.getLinkProps({
        entity,
        entityType,
        onlyLinkProps: true,
        pro: true,
      }),
    );
    track.click(
      {
        name: TRACK_EVENTS.CLICK,
        data: {
          id: ARTIST_PRO_VIEW.ARTIST_PRO_VIEW_SWITCH,
          tab: 'pro',
        },
      },
      GOOGLE_OLD_TRACKING_SERVICES,
    );
  }, [entity, entityType, navigate, track]);

  const actionItems = useActionItems({
    trackingConfig: trackingData,
    onClickHandler,
  });

  if (isMobile) {
    return (
      <div className={classes.stickyHeader}>
        <div className={classes.stickyHeader__container}>
          <div className={classes.stickyHeader__castingTop}>
            <Profile entity={entity} />
            <KebabMenu options={actionItems} />
          </div>
          <div>
            <HorizontalScroller hideArrow>
              <div className={classes.stickyHeader__bottom}>
                <Filters layout={FILTER_LAYOUT.ICON} trackingData={trackingData} />
                <TabsNavigation
                  tabs={tabs}
                  activeTab={activeTab}
                  trackingData={{
                    ...trackingData,
                    component: COMPONENTS.TABS,
                  }}
                  ns="NS_ENTITY_STUB_PAGE"
                  handleOnClick={tab => {
                    navigate.to(tab?.linkProps);
                    navigate.scrollTo({ behavior: 'auto' });
                  }}
                  variant="dark"
                  asDropdown
                />
                {!(entityType === ENTITY_TYPES.ORGANIZATION && 
                   !entity?.stats?.productions?.exists) && (
                  <QuickFilters
                    filterType={FILTER_TYPES.SEASON}
                    limit={15}
                    scrollable
                    includeSpecialOptions
                    skipBorder
                    trackingData={{
                      ...trackingData,
                      component: COMPONENTS.QUICK_FILTERS,
                    }}
                  />
                )}
              </div>
            </HorizontalScroller>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.stickyHeader}>
      <div
        className={classnames(classes.stickyHeader__container, {
          [classes.stickyHeader__containerCalendar]: showCalendarFilters,
        })}
      >
        <div className={classes.stickyHeader__castingTop}>
          <Profile entity={entity} />
          <ShareEntity
            showLabelText={false}
            className={classes.shareEntity}
            trackingData={{ ...trackingData, component: COMPONENTS.SHARE_CTA }}
            sticky
          />
          <div key="bookmark" className={classes.bookmark__icon}>
            <SpriteIcon icon="bookmark_add" size={16} />
          </div>
          <div className={classes.divider} />
          <Filters layout={FILTER_LAYOUT.BUTTON} />
          <div className={classes.divider} />
          {isSmallScreen ? (
            <TabsNavigation
              tabs={tabs}
              activeTab={activeTab}
              ns="NS_ENTITY_STUB_PAGE"
              handleOnClick={tab => {
                navigate.to(tab?.linkProps);
                navigate.scrollTo({ behavior: 'auto' });
              }}
              variant="dark"
              asDropdown
              trackingData={{
                ...trackingData,
                component: COMPONENTS.TABS,
              }}
            />
          ) : (
            <HorizontalScroller styles={{ root: classes.horizontalScroller }} hideArrow>
              <div className={classes.tabs}>
                {tabs.map(tab => (
                  <LinkButton
                    variant="text"
                    styles={{
                      root: classnames({
                        [classes.tab__active]: activeTab === tab?.key,
                      }),
                    }}
                    disableUnderline
                    isLink
                    onClick={() => {
                      navigate.scrollTo({ behavior: 'auto' });
                    }}
                    trackingData={{
                      ...trackingData,
                      component: COMPONENTS.TABS,
                    }}
                    {...tab.linkProps}
                  >
                    <Typography
                      size="13"
                      className={classnames({
                        [classes.tab__activeLabel]: activeTab === tab?.key,
                      })}
                    >
                      {t(tab?.title)}
                    </Typography>
                  </LinkButton>
                ))}
              </div>
            </HorizontalScroller>
          )}
          {entityType === ENTITY_TYPES.PROFILE && (
            <CastingToolPromo
              entityType={entityType}
              entity={entity}
              onClick={onClickHandler}
              asLabel={isSmallScreen}
              styles={{ castingToolCta: classes.castingToolPromo }}
              trackingData={{
                ...trackingData,
                component: COMPONENTS.VIEW_IN_CASTING_TOOL_CTA,
              }}
              asCTA
            />
          )}
          {!isLoggedIn && (
            <PrimaryButton
              onClick={() => setIsLoginDialog({ isOpen: true })}
              shape="rectangle"
              size="medium"
              trackingData={{
                ...trackingData,
                subComponent: SUB_COMPONENTS.LOGIN_CTA,
              }}
              leftIcon={<SpriteIcon icon="account_circle" size={14} />}
            >
              {t(`${TP}.m_LOGIN`)}
            </PrimaryButton>
          )}
        </div>

        {hasAppliedFilters && (
          <div className={classes.stickyHeader__middle}>
            <AppliedFilters trackingData={trackingData} />
          </div>
        )}
        {showCalendarFilters ? (
          <div className={classes.stickyHeader__castingBottom}>
            <FilterFacets
              layout={FILTER_LAYOUT.BUTTON_GROUP_DETAILED}
              scrollable
              customGroup={customFilterGroup}
              styles={{ root: classes.filterContainerDetailed, filterGroupBox: classes.filterGroupBox }}
            />
            <div className={classes.stickyHeaderContent__filters_calendar}>
              <CalendarRangeNavigation
                viewType={viewType}
                startDate={rangeStartDate}
                endDate={rangeEndDate}
                onShiftLeft={onShiftLeft}
                onShiftRight={onShiftRight}
                allowLeftShift={allowLeftShift}
                allowRightShift={allowRightShift}
              />
              <CalendarHeaders viewType={viewType} rangeStartDate={rangeStartDate} />
            </div>
          </div>
        ) : (
          !(entityType === ENTITY_TYPES.ORGANIZATION && 
            !entity?.stats?.productions?.exists) && (
            <QuickFilters
              filterType={FILTER_TYPES.SEASON}
              limit={15}
              collapsable
              includeSpecialOptions
              skipBorder
              trackingData={{
                ...trackingData,
                component: COMPONENTS.QUICK_FILTERS,
              }}
            />
          )
        )}
      </div>
    </div>
  );
};

export default PublicViewStickyHeader;
